import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { AppleIcon, PlayIcon } from "../components/StoreBadges";

const APP_STORE_URL = "https://apps.apple.com/us/app/pahincho/id6788609680";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.pahincho.pahincho";

/**
 * Fallback page for https://www.pahincho.com/q/:code
 * When Universal Links / App Links work, the OS opens the app and this page
 * never shows. Otherwise we show store links; opening the app is opt-in only
 * so users without the app are not hit with system "open application" prompts.
 */
const ItemLabelOpen: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const itemId = (code || "").trim();

  const openInAppUrl = useMemo(() => {
    if (!itemId) return "";
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isAndroid) {
      const fallback = encodeURIComponent(
        `https://www.pahincho.com/q/${encodeURIComponent(itemId)}`,
      );
      return `intent://q/${encodeURIComponent(itemId)}#Intent;scheme=pahincho;package=com.pahincho.pahincho;S.browser_fallback_url=${fallback};end`;
    }
    return `pahincho:///q/${encodeURIComponent(itemId)}`;
  }, [itemId]);

  if (!itemId) {
    return (
      <main style={styles.main}>
        <h1 style={styles.h1}>Invalid label</h1>
        <p style={styles.p}>This QR code link is missing an item id.</p>
        <Link to="/">Go to pahincho.com</Link>
      </main>
    );
  }

  return (
    <main style={styles.main}>
      <h1 style={styles.h1}>Get the Pahincho app</h1>
      <p style={styles.p}>
        Download it from the App Store or Google Play, then come back to this
        link to view the item.
      </p>

      <div style={styles.stores}>
        <a href={APP_STORE_URL} style={styles.store} target="_blank" rel="noreferrer">
          <AppleIcon />
          <span>App Store</span>
        </a>
        <a href={PLAY_STORE_URL} style={styles.store} target="_blank" rel="noreferrer">
          <PlayIcon />
          <span>Google Play</span>
        </a>
      </div>

      <p style={styles.retry}>
        Already have the app?{" "}
        <a href={openInAppUrl} style={styles.retryLink}>
          Try again
        </a>
      </p>

      <p style={styles.muted}>Item: {itemId}</p>
    </main>
  );
};

const styles: Record<string, React.CSSProperties> = {
  main: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    fontFamily:
      'Montserrat, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: "#FFF8F0",
    textAlign: "center",
  },
  h1: { fontSize: 22, margin: "0 0 8px", color: "#1D1D1F" },
  p: { margin: "0 0 20px", color: "#555", maxWidth: 400, lineHeight: 1.45 },
  stores: { display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" },
  store: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 999,
    border: "1px solid #E5E5E5",
    background: "#fff",
    color: "#1D1D1F",
    textDecoration: "none",
    fontSize: 14,
  },
  retry: { marginTop: 20, fontSize: 13, color: "#777" },
  retryLink: { color: "#FF9500", textDecoration: "none", fontWeight: 600 },
  muted: { marginTop: 24, fontSize: 12, color: "#999", wordBreak: "break-all" },
};

export default ItemLabelOpen;
